/**
 * HTTP client for the Cloudflare Sandbox Worker API.
 *
 * Uses the standard Fetch API -- no Node.js-specific dependencies.
 * Note: "exec" and "run" refer to Sandbox Worker HTTP endpoints, not child_process.
 *
 * Usage:
 *   const client = createSandboxClient('https://sandbox.example.com', token);
 *   const info = await client.createSandbox({ repo: 'owner/repo' });
 */

import { SandboxApiError } from './types.js';
import type {
  ExecRequest,
  ExecResponse,
  SandboxClient,
  SandboxCreateRequest,
  SandboxInfo,
  SSEEvent,
} from './types.js';

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create a new {@link SandboxClient} that talks to the Sandbox Worker at
 * `baseUrl` using the given Bearer `token` for authentication.
 */
export function createSandboxClient(
  baseUrl: string,
  token?: string,
  options: { actingUserId?: string } = {},
): SandboxClient {
  const actingUserId = options.actingUserId;
  // Strip trailing slash from baseUrl for consistent URL building
  const base = baseUrl.replace(/\/+$/, '');

  // -----------------------------------------------------------------------
  // Internal helpers
  // -----------------------------------------------------------------------

  /** Make an authenticated JSON request to the Sandbox Worker HTTP API. */
  async function request<T>(method: string, path: string, body?: unknown, timeoutMs = 30_000, externalSignal?: AbortSignal): Promise<T> {
    const controller = new AbortController();
    const timeoutId = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
    // Forward external abort (e.g. from SIGINT handler) to our controller
    if (externalSignal) {
      if (externalSignal.aborted) { controller.abort(); }
      else { externalSignal.addEventListener('abort', () => controller.abort(), { once: true }); }
    }
    try {
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (actingUserId) {
        headers['X-Acting-User-Id'] = actingUserId;
      }

      const init: RequestInit = { method, headers, signal: controller.signal };

      if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }

      const res = await fetch(`${base}${path}`, init);

      if (!res.ok) {
        const text = await res.text();
        throw new SandboxApiError(`Sandbox API ${method} ${path} failed (${res.status}): ${text}`, res.status);
      }

      return res.json() as Promise<T>;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        if (externalSignal?.aborted) {
          throw Object.assign(new Error(`Sandbox API request aborted: ${method} ${path}`), { name: 'AbortError' });
        }
        throw new Error(`Sandbox API request timed out after ${timeoutMs}ms: ${method} ${path}`);
      }
      throw err;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // -----------------------------------------------------------------------
  // SSE parser
  // -----------------------------------------------------------------------

  async function consumeSSE(
    body: ReadableStream<Uint8Array>,
    onEvent: (event: SSEEvent) => void,
  ): Promise<void> {
    const decoder = new TextDecoder();
    const reader = body.getReader();
    let buffer = '';

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on double-newline (SSE event boundary)
      const parts = buffer.split('\n\n');
      // The last element is either empty (if buffer ended with \n\n) or an
      // incomplete event -- keep it in the buffer.
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;

        let eventType = '';
        let eventData = '';

        for (const line of trimmed.split('\n')) {
          if (line.startsWith('event:')) {
            eventType = line.slice('event:'.length).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.slice('data:'.length).trim();
          }
        }

        if (eventType && eventData) {
          try {
            const parsed = JSON.parse(eventData);
            onEvent({ type: eventType, data: parsed } as SSEEvent);
          } catch (parseErr) {
            console.warn(`Malformed SSE event (type=${eventType}): ${eventData.substring(0, 200)}`);
          }
        }
      }
    }
  }

  // -----------------------------------------------------------------------
  // Public API  (HTTP endpoints on the Sandbox Worker, not child_process)
  // -----------------------------------------------------------------------

  return {
    // -- Lifecycle --------------------------------------------------------

    async createSandbox(options: SandboxCreateRequest): Promise<SandboxInfo> {
      return request<SandboxInfo>('POST', '/api/sandbox', options, 300_000);
    },

    async getSandboxStatus(id: string): Promise<SandboxInfo> {
      return request<SandboxInfo>('GET', `/api/sandbox/${id}/status`);
    },

    async destroySandbox(id: string): Promise<void> {
      // 60s timeout: the worker runs git-safety (up to 15s) + container destroy
      await request<unknown>('DELETE', `/api/sandbox/${id}`, undefined, 60_000);
    },

    // -- Task invocation (Sandbox Worker HTTP API) -------------------------

    async exec(options: ExecRequest, signal?: AbortSignal): Promise<ExecResponse> {
      // POST /api/exec on the Sandbox Worker (not child_process)
      return request<ExecResponse>('POST', '/api/exec', options, 0, signal);
    },

    // -- Streaming invocation (SSE) ----------------------------------------

    async run(options: ExecRequest, onEvent: (event: SSEEvent) => void, signal?: AbortSignal): Promise<void> {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (actingUserId) {
        headers['X-Acting-User-Id'] = actingUserId;
      }

      const res = await fetch(`${base}/api/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify(options),
        signal,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new SandboxApiError(`Sandbox API POST /api/run failed (${res.status}): ${text}`, res.status);
      }

      if (!res.body) {
        throw new Error('Sandbox API POST /api/run returned no body');
      }

      await consumeSSE(res.body, onEvent);
    },

    // -- URL builders -----------------------------------------------------

    getTerminalUrl(id: string): string {
      return `${base}/sandbox/${id}/terminal`;
    },

    getWsUrl(id: string): string {
      const wsBase = base
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://');
      return `${wsBase}/sandbox/${id}/ws`;
    },
  };
}
